import { Trans, useTranslation } from 'react-i18next';

import { ReactComponent as CheckIcon } from '../../../../assets/icons/check-circle.svg';
import { ReactComponent as DownloadIcon } from '../../../../assets/icons/icon-download.svg';
import type { IActivationPriceMismatch } from '../../../hooks/useActivationCensusFlow/activationCensus';
import type { IActivationCensusErrorsFile } from '../../../interfaces/quotes';
import { TRANSLATIONS_PROSPECTS } from '../../../translations/prospects';
import { formatCurrency } from '../../../utils/currency/currency';
import { downloadBase64File } from '../../../utils/utils';
import CustomButton from '../../Buttons/CustomButton/CustomButton';
import BannerFill from '../../Globals/BannerFill/BannerFill';
import Spinner from '../../Globals/Spinner/Spinner';

const MODAL = TRANSLATIONS_PROSPECTS.profile.activationSetup.censusModal;

interface DangerBannerProps {
  readonly title: string;
  readonly description?: string;
  readonly inline?: boolean;
}

// Every census error reads as a danger banner with the same screen-reader label.
function DangerBanner({ title, description, inline }: DangerBannerProps) {
  const { t } = useTranslation();
  return (
    <BannerFill
      variant="danger"
      inline={inline}
      title={title}
      description={description}
      screenReaderLabel={t(MODAL.error)}
    />
  );
}

interface AsyncStepProps {
  readonly title: string;
  readonly hint: string;
}

// Shared async placeholder for the `validating` and `processing` steps.
export function ActivationCensusAsyncStep({ title, hint }: AsyncStepProps) {
  return (
    <div className="activation-census-step activation-census-step--centered">
      <Spinner size={28} variant="svg" aria-label={title} />
      <div className="activation-census-step__title">{title}</div>
      <p className="activation-census-step__hint">{hint}</p>
    </div>
  );
}

interface SuccessStepProps {
  readonly employeesValidated?: number | null;
  readonly isAppOnly?: boolean;
}

const isCount = (value?: number | null): value is number => value !== null && value !== undefined;

export function ActivationCensusSuccessStep({ employeesValidated, isAppOnly }: SuccessStepProps) {
  const { t } = useTranslation();

  return (
    <div className="activation-census-step">
      <div className="activation-census-summary">
        {isCount(employeesValidated) && (
          <div className="activation-census-summary__row">
            <CheckIcon className="activation-census-summary__check" width={16} height={16} aria-hidden="true" />
            <span>
              <Trans
                i18nKey={MODAL.successEmployees}
                values={{ count: employeesValidated }}
                components={{ b: <b /> }}
              />
            </span>
          </div>
        )}
      </div>
      <p className="activation-census-step__hint">{t(isAppOnly ? MODAL.successLeadAppOnly : MODAL.successLead)}</p>
    </div>
  );
}

interface ErrorDataManyStepProps {
  readonly rowsWithErrors: number;
  readonly formatErrors: number;
  readonly emptyCellErrors: number;
  readonly errorsFile?: IActivationCensusErrorsFile | null;
}

export function ActivationCensusErrorDataManyStep({
  rowsWithErrors,
  formatErrors,
  emptyCellErrors,
  errorsFile
}: ErrorDataManyStepProps) {
  const { t } = useTranslation();

  const handleDownload = () => {
    if (!errorsFile) return;
    downloadBase64File(errorsFile.base64, errorsFile.fileName, errorsFile.mimeType);
  };

  const breakdown = [
    { key: 'format', count: formatErrors, label: t(MODAL.errorFormatCount, { count: formatErrors }) },
    { key: 'empty', count: emptyCellErrors, label: t(MODAL.errorEmptyCellCount, { count: emptyCellErrors }) }
  ].filter(({ count }) => count > 0);

  return (
    <div className="activation-census-step">
      <DangerBanner inline title={t(MODAL.errorManyBannerTitle, { count: rowsWithErrors })} />

      {breakdown.length > 0 && (
        <div className="activation-census-error-summary">
          <div className="activation-census-error-summary__label">{t(MODAL.errorSummary)}</div>
          <div className="activation-census-error-summary__list">
            {breakdown.map(({ key, label }) => (
              <div key={key} className="activation-census-error-summary__item">
                <span className="activation-census-error-summary__bullet">·</span> {label}
              </div>
            ))}
          </div>
        </div>
      )}
      {errorsFile && (
        <div className="activation-census-step__actions">
          <CustomButton variant="default" size="xs" onClick={handleDownload}>
            <DownloadIcon className="activation-census-step__download-icon" width={12} height={12} />
            {t(MODAL.downloadErrorReport)}
          </CustomButton>
        </div>
      )}
    </div>
  );
}

interface ErrorMinimumStepProps {
  readonly employeeCount: number;
  readonly minimum: number;
  readonly isAppOnly?: boolean;
}

export function ActivationCensusErrorMinimumStep({ employeeCount, minimum, isAppOnly }: ErrorMinimumStepProps) {
  const { t } = useTranslation();

  return (
    <div className="activation-census-step">
      <DangerBanner
        title={isAppOnly ? t(MODAL.errorMinimumBannerTitleAppOnly) : t(MODAL.errorMinimumBannerTitle, { hc: minimum })}
        description={
          isAppOnly
            ? t(MODAL.errorMinimumDescAppOnly)
            : t(MODAL.errorMinimumDesc, { count: employeeCount, hc: minimum })
        }
      />
    </div>
  );
}

export function ActivationCensusErrorServiceStep() {
  const { t } = useTranslation();

  return (
    <div className="activation-census-step">
      <DangerBanner title={t(MODAL.errorServiceTitle)} description={t(MODAL.errorServiceDesc)} />
      <div className="activation-census-step__hint">{t(MODAL.errorServiceNote)}</div>
    </div>
  );
}

interface PriceCardProps {
  readonly label: string;
  readonly amount: number;
  /** The recalculated price, highlighted in brand colours. */
  readonly highlighted?: boolean;
}

function PriceCard({ label, amount, highlighted }: PriceCardProps) {
  const { t } = useTranslation();
  const className = `activation-census-price__card${highlighted ? ' activation-census-price__card--new' : ''}`;

  return (
    <div className={className}>
      <div className="activation-census-price__label">{label}</div>
      <div className="activation-census-price__value">{formatCurrency(amount)}</div>
      <div className="activation-census-price__sub">{t(MODAL.priceMismatchPerMonth)}</div>
    </div>
  );
}

interface PriceMismatchStepProps {
  readonly payload: IActivationPriceMismatch;
}

export function ActivationCensusPriceMismatchStep({ payload }: PriceMismatchStepProps) {
  const { t } = useTranslation();

  return (
    <div className="activation-census-step">
      <p className="activation-census-step__hint">{t(MODAL.priceMismatchLead)}</p>
      <div className="activation-census-price">
        <PriceCard highlighted label={t(MODAL.priceMismatchNew)} amount={payload.newPrice} />
        <PriceCard label={t(MODAL.priceMismatchOriginal)} amount={payload.originalPrice} />
      </div>
      <div className="activation-census-price-reason">
        <div className="activation-census-price-reason__label">{t(MODAL.priceMismatchReason)}</div>
        <p className="activation-census-price-reason__text">{payload.reason}</p>
      </div>
    </div>
  );
}
